import { RestRequest, RestContext, ResponseFunction } from 'msw'
import { getCompanyLastSelectedStateDataByCompanyId } from '../companyLastSelectedStates/mockData'
import { getCompanyContactListDataByCompanyId } from '../companyContacts/mockData'
import { getLocationListDataByCompanyId } from '../locations/mockData'
import { getContractListDataByCompanyId } from '../contracts/mockData'
import { updateCompanyInOpportunities } from '../opportunities/mockData'
import { updateCompanyInOrders } from '../orders/mockData'
import { requireAuthenticatedUser } from '../../decorators'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
  addNewEntityDataToEntityListData,
} from '../../modules/mockDataCommon'

import { createMockOperation } from '../operations'
import { getCompanyMSServiceListData } from '../companyMSServices/mockData'
import { getCompanyMasterServiceListData } from '../companyMasterServices/mockData'
import {
  getCompanyListData,
  getCompanyDataById,
  getCompanyOverviewData,
} from './mockData'
import { assertIsNotUndefined } from '~/types/models'
import type { THridAndVersion } from '~/api/msw/modules/mockDataCommon'
import type { ICompanyOverviewForPost } from '~/cynqueModules/dataObjects/Order'
import { getAllMSServicesFromSiloOverview } from '~/cynqueModules/siloRelated/getAllMSServicesFromSiloOverview'
import type {
  ICompany,
  ICompanyOverview,
  ICompanyMasterServiceForPost,
  ICompanyMSServiceForPost,
  ICompanyLastSelectedState,
} from '~/types/company/models'

interface GetCompaniesResponse {
  data: ICompany[]
}

// get

const getCompanies = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const responseBody: GetCompaniesResponse = {
    data: await getCompanyListData(),
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

const getCompanies500 = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const responseBody = { message: 'A mock server-side error happened.' }
  return res(ctx.status(500), ctx.json(responseBody))
}

const getCompanies422 = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const responseBody = {
    message: 'A mock 422: Unprocessable Entity error happened.',
  }
  return res(ctx.status(422), ctx.json(responseBody))
}

const getCompanies403 = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const responseBody = { message: 'A mock 403: Forbidden error happened.' }
  return res(ctx.status(403), ctx.json(responseBody))
}

async function getCompany(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning data for company id "${id}".`)
  const data = await getCompanyDataById(id)
  return res(ctx.status(200), ctx.json({ data }))
}

interface GetCompanyOverviewResponse {
  data: ICompanyOverview
}

const getCompanyOverview = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetCompanyOverviewResponse = {
      data: await getCompanyOverviewData(id)!, // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e) {
    const responseBody = {
      statusCode: 404,
      message: `Unable to retrieve company overview for '${id}'`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

interface GetCompanyLastSelectedStateResponse {
  data: ICompanyLastSelectedState
}

async function getCompanyLastSelectedStateById(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetCompanyLastSelectedStateResponse = {
      data: await getCompanyLastSelectedStateDataByCompanyId(id)!, // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't get Company ${id} last selected data: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

const getCompanyOverview500 = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const responseBody = { message: 'A mock server-side error happened.' }
  return res(ctx.status(500), ctx.json(responseBody))
}

async function getCompanyContactsByCompanyId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning contacts for company "${id}".`)
  const data = await getCompanyContactListDataByCompanyId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

async function getCompanyLocationsByCompanyId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning company locations for company "${id}".`)
  const data = await getLocationListDataByCompanyId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

async function getContractsByCompanyId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning company contracts for company "${id}".`)
  const data = await getContractListDataByCompanyId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

// post

const postCompany = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(req, res, ctx, getCompanyListData)
}

type PostResponse = THridAndVersion

async function postCompanyOverview(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const fn = 'postCompanyOverview |'
  const overviewData: ICompanyOverviewForPost = await req.json()
  console.debug(`${fn} overviewData:`, overviewData)

  // company
  assertIsNotUndefined(overviewData.company.id) // Should never be. (This is a type guard.)
  const preexistingCompanyOverviewData = await getCompanyOverviewData(
    overviewData.company.id
  )
  console.debug(
    `${fn} preexising companyOverview:`,
    preexistingCompanyOverviewData
  )
  assertIsNotUndefined(preexistingCompanyOverviewData) // guard; should never fail
  const allExistingCompanyMSServices = getAllMSServicesFromSiloOverview({
    overviewData: preexistingCompanyOverviewData,
  })
  const allExistingCompanyMSServicesIds =
    allExistingCompanyMSServices?.map((cmss) => cmss?.id) ?? []

  // companyMSServices (has to preceed companyMasterServices
  // cause companyMSServices references them)
  const allMSServicesToPost: ICompanyMSServiceForPost[] =
    overviewData.companyMasterServices
      .filter((ms: ICompanyMasterServiceForPost) => !!ms.companyMSServices)
      .map((ms: ICompanyMasterServiceForPost) => ms.companyMSServices)
      .flat()
      .filter(
        (ms: ICompanyMSServiceForPost) =>
          !allExistingCompanyMSServicesIds.includes(ms.id)
      )

  if (!allMSServicesToPost || allMSServicesToPost?.length < 1) {
    console.debug(
      `${fn} All msServices were already posted. This typically shouldn't happen.`
    )
    return res(ctx.status(201))
  }
  console.debug(
    `${fn} There are msServices to add to existing companyMSServices. Proceeding.`,
    allMSServicesToPost
  )
  const orderHridAndVersion = undefined
  const msServicesHridAndVersionArray = await addNewEntityDataToEntityListData({
    req,
    entityKeyBaseOverride: 'companymsservices',
    newEntityData: allMSServicesToPost,
    getEntityListDataFn: getCompanyMSServiceListData,
  })

  // companyMasterServices
  console.debug(
    `${fn} Setting companyMasterServicesToAdd, with starting array from order of:`,
    overviewData.companyMasterServices
  )
  const companyMasterServicesToAdd = overviewData.companyMasterServices.filter(
    (cms) =>
      !preexistingCompanyOverviewData?.companyMasterServices.some(
        (pecms) => pecms.id === cms.id
      )
  )
  console.debug(
    `${fn} companyMasterServicesToAdd, after filtering`,
    companyMasterServicesToAdd
  )
  const masterServicesHridAndVersionArray =
    await addNewEntityDataToEntityListData({
      req,
      entityKeyBaseOverride: 'companymasterservices',
      newEntityData: companyMasterServicesToAdd,
      getEntityListDataFn: getCompanyMasterServiceListData,
    })

  // contracts (update: add company.id )
  // await updateEntityListDataMultipleFromUpdatedEntityData({
  //   req, // request is made to the companys api, so request contains company.id
  //   entityKeyBaseOverride: 'contracts',
  //   updatedEntityDataMultiple: overviewData.contracts,
  //   getEntityListDataFn: getContractListData,
  // })

  const responseBody: PostResponse = masterServicesHridAndVersionArray // TEMP WHILE IMPLEMENTING
  const idempotencyKey = req.headers.get('Idempotency-Key')
  if (idempotencyKey) {
    const operationId = createMockOperation(
      'POST /api/companies/overview',
      responseBody
    )
    return res(
      ctx.status(202),
      ctx.json({
        data: {
          operation_id: operationId,
          status: 'pending',
          operation_type: 'POST /api/companies/overview',
        },
      })
    )
  }
  return res(ctx.status(201), ctx.json(responseBody))
}

const patchCompany = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getCompanyListData, [
    updateCompanyInOpportunities,
    updateCompanyInOrders,
  ])
}

const deleteCompany = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getCompanyListData)
}

export const handlers = [
  {
    method: 'get',
    path: '/api/companies',
    handler: getCompanies,
  },
  {
    method: 'get',
    path: '/api/companies/:id',
    handler: getCompany,
  },
  {
    method: 'get',
    path: '/api/companies/:id/overview',
    handler: getCompanyOverview,
  },
  {
    method: 'get',
    path: '/api/companies/:id/lastselectedstate',
    handler: getCompanyLastSelectedStateById,
  },
  {
    method: 'get',
    path: '/api/companies/:id/contacts',
    handler: getCompanyContactsByCompanyId,
  },
  {
    method: 'get',
    path: '/api/companies/:id/locations',
    handler: getCompanyLocationsByCompanyId,
  },
  {
    method: 'get',
    path: '/api/companies/:id/contracts',
    handler: getContractsByCompanyId,
  },
  {
    method: 'post',
    path: '/api/companies',
    handler: postCompany,
  },
  {
    method: 'post',
    path: '/api/companies/overview',
    handler: postCompanyOverview,
  },
  {
    method: 'patch',
    path: '/api/companies/:id',
    handler: patchCompany,
  },
  {
    method: 'delete',
    path: '/api/companies/:id',
    handler: deleteCompany,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
